import { Field, Formik, Form as FormikForm } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineWhatsApp
} from 'react-icons/ai';
import { BiBuildingHouse, BiTime } from 'react-icons/bi';
import { BsHouseDoor, BsPencil } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import Creatable from 'react-select/creatable';
import Select from 'react-select';
import {
  updateBranchOffice,
  updateProjectsStatus,
  updatePropertiesStatus
} from 'redux/branchOfficeSlice';
import ButtonPrimarySubmit from 'utils/buttons/ButtonPrimarySubmit';
import { formSchema } from '../validations/formSchema';
import ButtonActive from 'utils/buttons/ButtonActive';
import ButtonPause from 'utils/buttons/ButtonPause';
import ButtonDelete from 'utils/buttons/ButtonDelete';
import ModalActions from './ModalActions';
import messageHandler from 'utils/messageHandler';
import {
  getRealEstateProjectStats,
  getRealEstatePropStats
} from 'redux/realEstateSlice';
import ModalActionDelete from './ModalActionDelete';

const getInput = ({
  type,
  name,
  touched,
  errors,
  isDisabled,
  placeholder,
  values,
  setValues
}) => {
  const { neighborhoods } = useSelector(state => state.props);
  const [isError, setIsError] = useState(false);

  switch (type) {
    case 'select':
      return (
        <Select
          name={name}
          value={neighborhoods.find(option => option.value === values[name])}
          onChange={option => {
            setValues({
              ...values,
              [name]: option ? option.value : ''
            });
          }}
          options={neighborhoods.map(neighborhood => ({
            value: neighborhood.value,
            label: neighborhood.label
          }))}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isSearchable={false}
          styles={{
            menu: base => ({ ...base, zIndex: 9999 }),
            control: base => ({
              ...base,
              borderColor:
                touched[name] && !!errors[name] ? 'red' : base.borderColor,
              boxShadow:
                touched[name] && !!errors[name]
                  ? '0 0 0 1px red'
                  : base.boxShadow
            })
          }}
        />
      );

    case 'extraPhones':
      return (
        <>
          <Creatable
            classNames={{
              control: () => 'form-control',
              dropdownIndicator: () => 'd-none',
              clearIndicator: () => 'd-none',
              indicatorSeparator: () => 'd-none',
              input: () => 'p-0',
              valueContainer: () => 'p-0'
            }}
            classNamePrefix="react-select"
            isMulti
            placeholder={placeholder}
            noOptionsMessage={() => 'Escriba para crear un nuevo teléfono'}
            formatCreateLabel={inputValue => `Crear teléfono: ${inputValue}`}
            value={values.extraPhones}
            onChange={selected => {
              const isLong = selected.some(
                phone => phone.label.length < 8 || phone.label.length > 13
              );
              if (isLong) {
                setIsError(true);
                setTimeout(() => {
                  setIsError(false);
                }, 5000);
              } else {
                setValues({
                  ...values,
                  extraPhones: selected
                });
              }
            }}
          />
          {isError && (
            <div
              style={{
                marginTop: '0.25rem',
                fontSize: '75%',
                color: '#e63757'
              }}
            >
              Los números deben tener entre 8 y 13 dígitos
            </div>
          )}
        </>
      );

    default:
      return (
        <Form.Control
          as={Field}
          name={name}
          isInvalid={touched[name] && !!errors[name]}
          disabled={isDisabled}
          type={type}
          placeholder={placeholder}
        />
      );
  }
};

const InputBranchOffice = ({
  label,
  name,
  Icon,
  placeholder,
  touched,
  errors,
  isDisabled,
  type,
  options,
  values,
  setValues
}) => (
  <Col xs={12}>
    <Form.Group>
      <Form.Label>
        <span className="logo-normal">
          <Icon /> {label}
        </span>
      </Form.Label>

      {getInput({
        type,
        name,
        touched,
        errors,
        isDisabled,
        options,
        placeholder,
        values,
        setValues
      })}

      <Form.Control.Feedback type="invalid">
        {errors[name]}
      </Form.Control.Feedback>
    </Form.Group>
  </Col>
);

InputBranchOffice.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  placeholder: PropTypes.string.isRequired,
  touched: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
  type: PropTypes.string.isRequired,
  options: PropTypes.array,
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired
};

export const EditBranchOfficeForm = ({ branchOffice }) => {
  // * States
  const dispatch = useDispatch();
  const { realEstate } = useSelector(state => state.realEstate);
  const [configModal, setConfigModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const userLogged = useSelector(state => state.login.currentUser);

  const inputs = [
    {
      label: 'Dirección',
      name: 'address',
      Icon: BsHouseDoor,
      placeholder: 'Ingrese una dirección',
      isDisabled: userLogged?.typeOfUser !== 'cucicba',
      type: 'text'
    },
    {
      label: 'Barrio',
      name: 'barrio',
      Icon: BiBuildingHouse,
      placeholder: 'Seleccione un barrio',
      type: 'select'
    },
    {
      label: 'Email',
      name: 'email',
      Icon: AiOutlineMail,
      placeholder: 'Ingrese un email',
      type: 'text'
    },
    {
      label: 'Horarios de atención',
      name: 'openingHours',
      Icon: BiTime,
      placeholder: 'Seleccione los horarios de atención',
      type: 'text'
    },
    {
      label: 'Teléfono',
      name: 'phoneNumber',
      Icon: AiOutlinePhone,
      placeholder: 'Ingrese un teléfono',
      isDisabled: userLogged?.typeOfUser !== 'cucicba',
      type: 'text'
    },
    {
      label: 'Whatsapp',
      name: 'whatsapp',
      Icon: AiOutlineWhatsApp,
      placeholder: 'Ingrese un número de whatsapp',
      type: 'text'
    },
    {
      label: 'Teléfonos adicionales',
      name: 'extraPhones',
      Icon: AiOutlinePhone,
      placeholder: 'Ingrese teléfonos adicionales',
      type: 'extraPhones'
    }
  ];

  const actions = {
    ActivateProps: {
      action: () => ActivateProps(),
      title: 'Activar propiedades',
      bodyText:
        'Va a activar TODAS las propiedades que estén pausadas y que correspondan a la sucursal',
      buttonText: 'Activar'
    },
    PauseProps: {
      action: () => PauseProps(),
      title: 'Pausar propiedades',
      bodyText:
        'Va a pausar TODAS las propiedades que estén publicadas y que correspondan a la sucursal',
      buttonText: 'Pausar'
    },
    DeleteProps: {
      action: () => DeleteProps(),
      title: 'Eliminar propiedades (acción irreversible)',
      bodyText:
        'Va a eliminar TODAS las publicaciones correspondientes a la sucursal',
      buttonText: 'Eliminar'
    },
    ActivateProjects: {
      action: () => ActivateProjects(),
      title: 'Activar emprendimientos',
      bodyText:
        'Va a activar TODOS los emprendimientos que estén pausados y que correspondan a la sucursal',
      buttonText: 'Activar'
    },
    PauseProjects: {
      action: () => PauseProjects(),
      title: 'Pausar emprendimientos',
      bodyText:
        'Va a pausar TODOS los emprendimientos que estén publicados y que correspondan a la sucursal',
      buttonText: 'Pausar'
    },
    DeleteProjects: {
      action: () => DeleteProjects(),
      title: 'Eliminar emprendimientos (acción irreversible)',
      bodyText:
        'Va a eliminar TODOS los emprendimientos correspondientes a la sucursal',
      buttonText: 'Eliminar'
    }
  };

  const initialValues = {
    address: branchOffice.address || '',
    barrio: branchOffice.barrio || '',
    email: branchOffice.email || '',
    openingHours: branchOffice.openingHours || '',
    extraPhones: branchOffice.extraPhones,
    phoneNumber: branchOffice.phoneNumber || '',
    whatsapp: branchOffice.whatsapp || '',
    name: branchOffice.branch_office_name || ''
  };

  // * Methods
  const ActivateProps = async () => {
    setLoading(true);
    const response = await dispatch(
      updatePropertiesStatus(branchOffice.id, 'published')
    );
    if (response.status < 300) {
      messageHandler(
        'success',
        'Todas las propiedades se activaron correctamente'
      );
      setConfigModal(null);
      dispatch(getRealEstatePropStats(realEstate.id));
    } else
      messageHandler(
        'error',
        'Hubo un error al activar las propiedades. Por favor, intente nuevamente.'
      );
    setLoading(false);
  };

  const PauseProps = async () => {
    setLoading(true);
    const response = await dispatch(
      updatePropertiesStatus(branchOffice.id, 'paused')
    );
    if (response.status < 300) {
      messageHandler(
        'success',
        'Todas las propiedades se pausaron correctamente'
      );
      setConfigModal(null);
      dispatch(getRealEstatePropStats(realEstate.id));
    } else
      messageHandler(
        'error',
        'Hubo un error al pausar las propiedades. Por favor, intente nuevamente.'
      );
    setLoading(false);
  };

  const DeleteProps = async () => {
    setLoading(true);
    const response = await dispatch(
      updatePropertiesStatus(branchOffice.id, 'deleted')
    );
    if (response.status < 300) {
      messageHandler(
        'success',
        'Todas las publicaciones se eliminaron correctamente'
      );
      setDeleteModal(null);
      dispatch(getRealEstatePropStats(realEstate.id));
    } else
      messageHandler(
        'error',
        'Hubo un error al eliminar las publicaciones. Por favor, intente nuevamente.'
      );
    setLoading(false);
  };

  const ActivateProjects = async () => {
    setLoading(true);
    const response = await dispatch(
      updateProjectsStatus(branchOffice.id, 'published')
    );
    if (response.status < 300) {
      messageHandler(
        'success',
        'Todos los emprendimientos se activaron correctamente'
      );
      setConfigModal(null);
      dispatch(getRealEstateProjectStats(realEstate.id));
    } else
      messageHandler(
        'error',
        'Hubo un error al activar los emprendimientos. Por favor, intente nuevamente.'
      );
    setLoading(false);
  };

  const PauseProjects = async () => {
    setLoading(true);
    const response = await dispatch(
      updateProjectsStatus(branchOffice.id, 'paused')
    );
    if (response.status < 300) {
      messageHandler(
        'success',
        'Todos los emprendimientos se pausaron correctamente'
      );
      setConfigModal(null);
      dispatch(getRealEstateProjectStats(realEstate.id));
    } else
      messageHandler(
        'error',
        'Hubo un error al pausar los emprendimientos. Por favor, intente nuevamente.'
      );
    setLoading(false);
  };

  const DeleteProjects = async () => {
    setLoading(true);
    const response = await dispatch(
      updateProjectsStatus(branchOffice.id, 'deleted')
    );
    if (response.status < 300) {
      messageHandler(
        'success',
        'Todos los emprendimientos se eliminaron correctamente'
      );
      setDeleteModal(null);
      dispatch(getRealEstateProjectStats(realEstate.id));
    } else
      messageHandler(
        'error',
        'Hubo un error al eliminar los emprendimientos. Por favor, intente nuevamente.'
      );
    setLoading(false);
  };

  return (
    <>
      <Card className="bg-white">
        {userLogged?.typeOfUser !== 'cucicba' && (
          <Card.Header className="border-bottom">
            <Card.Title>{branchOffice.branch_office_name}</Card.Title>
          </Card.Header>
        )}
        <Formik
          initialValues={initialValues}
          onSubmit={values => {
            dispatch(updateBranchOffice(branchOffice.id, values));
          }}
          validationSchema={formSchema}
        >
          {({ errors, touched, setValues, values }) => {
            useEffect(() => {
              setValues(initialValues);
            }, [branchOffice]);

            return (
              <>
                <FormikForm>
                  {userLogged?.typeOfUser === 'cucicba' && (
                    <Card.Header className="border-bottom">
                      <Card.Title>
                        <Form.Label className="text-dark mb-2">
                          <BsPencil size={14} /> Nombre de la sucursal:
                        </Form.Label>
                        <Form.Control
                          as={Field}
                          name="name"
                          isInvalid={touched.name && !!errors.name}
                          type="input"
                          placeholder={values.name}
                          className="mb-2"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Card.Title>
                    </Card.Header>
                  )}

                  <Card.Body>
                    <Row className="g-3">
                      {inputs.map((input, index) => (
                        <Col xs={12} md={6} key={index}>
                          <InputBranchOffice
                            {...input}
                            touched={touched}
                            errors={errors}
                            setValues={setValues}
                            values={values}
                          />
                        </Col>
                      ))}
                      {userLogged?.typeOfUser !== 'cucicba' && (
                        <Col xs={12} className="text-muted fs--2">
                          * Los datos de dirección y teléfono solo pueden
                          modificarse a través del departamento de Atención al
                          Matriculado. Para consultas enviar un mail a:
                          cabaprop@colegioinmobiliario.org.ar
                        </Col>
                      )}
                      <Col xs={12}>
                        <ButtonPrimarySubmit
                          text="Guardar cambios"
                          className="w-100 mt-3"
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </FormikForm>
              </>
            );
          }}
        </Formik>

        <hr />
        <Card.Body className="pt-0">
          <Row className="mb-3">
            <Col xs={12}>
              <Form.Label className="text-dark">
                Propiedades: Acciones Masivas
              </Form.Label>
              <p className="mt-0">
                Las siguientes acciones se aplicarán a TODAS las propiedades de
                esta sucursal.
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={4} className="mb-2">
              <ButtonActive
                text="Activar pausadas"
                className="w-100 bg-primary text-white"
                funcion={() => setConfigModal(actions.ActivateProps)}
                loading={loading.ActivateProps}
              />
            </Col>
            <Col xs={12} md={4} className="mb-2">
              <ButtonPause
                text="Pausar todas"
                className="w-100 bg-secondary text-white"
                funcion={() => setConfigModal(actions.PauseProps)}
              />
            </Col>
            <Col xs={12} md={4}>
              <ButtonDelete
                text="Eliminar todas"
                className="w-100"
                funcion={() => setDeleteModal(actions.DeleteProps)}
              />
            </Col>
          </Row>
        </Card.Body>

        <hr />
        <Card.Body className="pt-0">
          <Row>
            <Form.Label className="text-dark">
              Emprendimientos: Acciones Masivas
            </Form.Label>
            <p className="mt-0">
              Las siguientes acciones se aplicarán a TODOS los emprendimientos
              de esta sucursal.
            </p>

            <Col xs={12} md={4} className="mb-2">
              <ButtonActive
                text="Activar pausados"
                className="w-100 bg-primary text-white"
                funcion={() => setConfigModal(actions.ActivateProjects)}
              />
            </Col>
            <Col xs={12} md={4} className="mb-2">
              <ButtonPause
                text="Pausar todos"
                className="w-100 bg-secondary text-white"
                funcion={() => setConfigModal(actions.PauseProjects)}
              />
            </Col>
            <Col xs={12} md={4} className="mb-2">
              <ButtonDelete
                text="Eliminar todos"
                className="w-100"
                funcion={() => setDeleteModal(actions.DeleteProjects)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!!configModal && (
        <ModalActions
          show={!!configModal}
          handleClose={() => setConfigModal(null)}
          action={configModal.action}
          title={configModal.title}
          bodyText={configModal.bodyText}
          buttonText={configModal.buttonText}
          branchName={branchOffice.branch_office_name}
          loading={loading}
        />
      )}

      {!!deleteModal && (
        <ModalActionDelete
          show={!!deleteModal}
          handleClose={() => setDeleteModal(null)}
          action={deleteModal.action}
          title={deleteModal.title}
          bodyText={deleteModal.bodyText}
          buttonText={deleteModal.buttonText}
          branchName={branchOffice.branch_office_name}
          loading={loading}
        />
      )}
    </>
  );
};
EditBranchOfficeForm.propTypes = {
  branchOffice: PropTypes.object.isRequired
};
